import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { registerPushServiceWorker } from "./lib/push";

import { AuthProvider } from "./contexts/AuthContext";
import { CompanyProvider } from "./contexts/CompanyContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import { ConfirmProvider } from "./components/ConfirmProvider";
import PwaInstallBanner from "./components/PwaInstallBanner";
import { Toaster } from "./components/ui/sonner";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import TrackPublic from "./pages/TrackPublic";
import AuthCallback from "./pages/AuthCallback";

import ClientHome from "./pages/client/Home";
import NewOrder from "./pages/client/NewOrder";
import Orders from "./pages/client/Orders";
import OrderDetail from "./pages/client/OrderDetail";
import Addresses from "./pages/client/Addresses";
import Profile from "./pages/client/Profile";
import PaymentSuccess from "./pages/client/PaymentSuccess";

import AdminDashboard from "./pages/admin/Dashboard";
import AdminOrders from "./pages/admin/Orders";
import AdminOrderDetail from "./pages/admin/OrderDetail";
import AdminReports from "./pages/admin/Reports";
import AdminProducts from "./pages/admin/Products";
import AdminBranding from "./pages/admin/Branding";
import AdminOffers from "./pages/admin/Offers";
import AdminDrivers from "./pages/admin/Drivers";
import AdminWaBot from "./pages/admin/WaBot";
import AdminRatings from "./pages/admin/Ratings";

import SuperAdminCompanies from "./pages/superadmin/Companies";

function RouterShell() {
  const location = useLocation();

  // Captura de atribución UTM: cuando el cliente llega con `?from=...`
  // (típicamente desde el link que enviamos en el primer mensaje del
  // bot de WhatsApp), persistimos el origen en `localStorage` para
  // adjuntarlo al primer pedido que cree. Se borra después de un
  // pedido — no atribuimos toda la vida del cliente al mismo canal.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const from = params.get("from");
    const allowed = new Set(["wa-bot", "wa-onboarding", "qr", "ads"]);
    if (from && allowed.has(from)) {
      try { localStorage.setItem("gas2go:source", from); } catch (_e) { /* ignore */ }
    }
  }, [location.search]);

  // Emergent Google Auth callback: session_id in URL fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<Navigate to="/app/orders" replace />} />
      <Route path="/track/:id" element={<TrackPublic />} />

      <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<ClientHome />} />
        <Route path="order/new" element={<NewOrder />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="addresses" element={<Addresses />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      <Route path="/admin" element={<ProtectedRoute roles={["company_admin"]}><AppShell /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="orders/:id" element={<AdminOrderDetail />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="branding" element={<AdminBranding />} />
        <Route path="offers" element={<AdminOffers />} />
        <Route path="drivers" element={<AdminDrivers />} />
        <Route path="wabot" element={<AdminWaBot />} />
        <Route path="ratings" element={<AdminRatings />} />
      </Route>

      <Route path="/super-admin" element={<ProtectedRoute roles={["super_admin"]}><AppShell /></ProtectedRoute>}>
        <Route index element={<SuperAdminCompanies />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // Registrar el Service Worker en cuanto la app carga — así los
  // navegadores ya tienen el SW listo cuando el cliente active push.
  // Silencioso: no pide permisos todavía, solo registra el archivo.
  useEffect(() => {
    registerPushServiceWorker();
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <CompanyProvider>
              <ConfirmProvider>
                <RouterShell />
                <PwaInstallBanner />
                <Toaster position="top-center" richColors closeButton />
              </ConfirmProvider>
            </CompanyProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}
